/**
 * ทะเบียน OAuth client ของ SSO (D-153) — `RBAC_ADMIN` เท่านั้น
 *
 * `client_secret` ที่ออกตอนสร้าง/หมุนอยู่ใน state ของ component นี้ที่เดียว แสดงครั้งเดียวใน
 * modal แล้วถูกล้างทันทีที่ปิด — ไม่เขียนลง storage, ไม่ log, ไม่ใส่ใน URL และไม่ปนเข้า
 * รายการ client (ซึ่งมาจาก `GET` ที่ไม่มี secret) สิทธิ์ `can_edit` กันปุ่มเขียน แต่ผู้ตัดสินจริงคือ backend
 */

import { useEffect, useState } from 'react';

import { ConfirmModal } from './ConfirmModal';
import { RbacAdminFailureNotice } from './RbacAdminFailureNotice';
import {
  REDIRECT_URI_KIND_LABELS,
  describeTtl,
  emptyOauthClientForm,
  formFromOauthClient,
  toCreateOauthClientBody,
  toUpdateOauthClientBody,
  type OauthClientFormState,
} from './oauthClientsView';
import { StatusBadge } from '../StatusBadge';
import { Modal } from '../components/Modal';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';
import {
  createOauthClient,
  fetchAppRoleCatalog,
  fetchOauthClients,
  rotateOauthClientSecret,
  updateOauthClient,
  type RbacAdminFailure,
} from '../../lib/rbacAdmin/client';
import {
  RBAC_ADMIN_MODULE,
  REDIRECT_URI_KINDS,
  type AppRoleCatalogEntry,
  type OauthClient,
  type RedirectUriKind,
} from '../../lib/rbacAdmin/types';

export interface OauthClientsScreenProps {
  readonly permissions: PermissionsState;
  readonly apiBaseUrl: string;
  readonly fetchImpl?: typeof fetch;
}

type ClientsState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly items: readonly OauthClient[] }
  | RbacAdminFailure;

type CatalogState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly items: readonly AppRoleCatalogEntry[] }
  | RbacAdminFailure;

/** `edit` ถือ client_id เดิม — `create` ไม่มี */
type FormMode =
  | { readonly kind: 'create' }
  | { readonly kind: 'edit'; readonly clientId: string; readonly original: OauthClient };

interface IssuedSecret {
  readonly clientId: string;
  readonly secret: string;
  readonly reason: 'created' | 'rotated';
}

export function OauthClientsScreen({ permissions, apiBaseUrl, fetchImpl }: OauthClientsScreenProps) {
  const canCreate = can(permissions, RBAC_ADMIN_MODULE, 'CREATE');
  const canEdit = can(permissions, RBAC_ADMIN_MODULE, 'EDIT');

  const [clients, setClients] = useState<ClientsState>({ status: 'loading' });
  const [catalog, setCatalog] = useState<CatalogState>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);

  const [formMode, setFormMode] = useState<FormMode | null>(null);
  const [form, setForm] = useState<OauthClientFormState>(emptyOauthClientForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [saveFailure, setSaveFailure] = useState<RbacAdminFailure | null>(null);

  const [rotateTarget, setRotateTarget] = useState<OauthClient | null>(null);
  const [rotateFailure, setRotateFailure] = useState<RbacAdminFailure | null>(null);
  const [issued, setIssued] = useState<IssuedSecret | null>(null);
  const [copied, setCopied] = useState(false);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    const options = { apiBaseUrl, fetchImpl, signal: controller.signal };

    setClients((previous) => (previous.status === 'ready' ? previous : { status: 'loading' }));

    void fetchOauthClients(options).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      setClients(result.status === 'ready' ? { status: 'ready', items: result.page.items } : result);
    });
    void fetchAppRoleCatalog(options).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      setCatalog(result.status === 'ready' ? { status: 'ready', items: result.page.items } : result);
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [apiBaseUrl, fetchImpl, reloadToken]);

  const catalogItems = catalog.status === 'ready' ? catalog.items : [];
  const appCodes = Array.from(new Set(catalogItems.map((entry) => entry.app_code)));
  const rolesOfApp = catalogItems.filter((entry) => entry.app_code === form.appCode);

  const openCreate = () => {
    setForm(emptyOauthClientForm());
    setFormError(null);
    setSaveFailure(null);
    setFormMode({ kind: 'create' });
  };

  const openEdit = (client: OauthClient) => {
    setForm(formFromOauthClient(client));
    setFormError(null);
    setSaveFailure(null);
    setFormMode({ kind: 'edit', clientId: client.client_id, original: client });
  };

  const closeForm = () => {
    setFormMode(null);
    setSaveFailure(null);
    setFormError(null);
  };

  const closeIssued = () => {
    setIssued(null);
    setCopied(false);
  };

  const patchForm = (patch: Partial<OauthClientFormState>) => {
    setForm((previous) => ({ ...previous, ...patch }));
  };

  const setRedirect = (index: number, patch: { uri?: string; kind?: RedirectUriKind }) => {
    setForm((previous) => ({
      ...previous,
      redirectUris: previous.redirectUris.map((entry, position) =>
        position === index ? { ...entry, ...patch } : entry,
      ),
    }));
  };

  const submitForm = async () => {
    if (formMode === null || pending || !canEdit) {
      return;
    }
    setFormError(null);
    setSaveFailure(null);

    if (formMode.kind === 'create') {
      const built = toCreateOauthClientBody(form);
      if (!built.ok) {
        setFormError(built.message);
        return;
      }
      setPending(true);
      const result = await createOauthClient(built.body, { apiBaseUrl, fetchImpl });
      setPending(false);
      if (result.status === 'cancelled') {
        return;
      }
      if (result.status !== 'ready') {
        setSaveFailure(result);
        return;
      }
      setFormMode(null);
      setCopied(false);
      setIssued({
        clientId: result.issued.client.client_id,
        secret: result.issued.client_secret,
        reason: 'created',
      });
      setReloadToken((token) => token + 1);
      return;
    }

    const built = toUpdateOauthClientBody(form);
    if (!built.ok) {
      setFormError(built.message);
      return;
    }
    setPending(true);
    const result = await updateOauthClient(formMode.clientId, built.body, { apiBaseUrl, fetchImpl }, formMode.original);
    setPending(false);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      setSaveFailure(result);
      return;
    }
    setFormMode(null);
    setReloadToken((token) => token + 1);
  };

  const confirmRotate = async () => {
    if (rotateTarget === null || pending || !canEdit) {
      return;
    }
    setPending(true);
    setRotateFailure(null);
    const result = await rotateOauthClientSecret(rotateTarget.client_id, { apiBaseUrl, fetchImpl });
    setPending(false);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      setRotateFailure(result);
      return;
    }
    setRotateTarget(null);
    setCopied(false);
    setIssued({
      clientId: result.issued.client.client_id,
      secret: result.issued.client_secret,
      reason: 'rotated',
    });
    setReloadToken((token) => token + 1);
  };

  const copySecret = async () => {
    if (issued === null) {
      return;
    }
    try {
      await navigator.clipboard.writeText(issued.secret);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const toggleMfaRole = (roleCode: string, checked: boolean) => {
    setForm((previous) => ({
      ...previous,
      mfaRoles: checked
        ? [...previous.mfaRoles, roleCode]
        : previous.mfaRoles.filter((entry) => entry !== roleCode),
    }));
  };

  const items = clients.status === 'ready' ? clients.items : [];

  return (
    <div className="cap-page" data-testid="oauth-clients">
      <div className="cap-page__head">
        <div>
          <h1 className="cap-page__title">OAuth client (SSO)</h1>
        </div>
        {canCreate ? (
          <button
            type="button"
            className="cap-button cap-button--primary"
            data-testid="oauth-client-create-open"
            onClick={openCreate}
          >
            สร้าง client
          </button>
        ) : null}
      </div>

      {!canEdit ? (
        <div className="cap-notice" data-testid="oauth-read-only" role="status">
          <StatusBadge tone="warning">ดูได้อย่างเดียว</StatusBadge>
          <p className="cap-notice__text">
            สิทธิ์ชุดนี้ไม่มี <code className="cap-table__code">can_edit</code> ของโมดูล{' '}
            <code className="cap-table__code">{RBAC_ADMIN_MODULE}</code> — ปุ่มแก้ไขและหมุน secret จึงไม่แสดง
          </p>
        </div>
      ) : null}

      {clients.status === 'loading' ? (
        <div className="cap-notice" data-testid="oauth-clients-loading" role="status">
          <p className="cap-notice__text">กำลังโหลดรายการ client…</p>
        </div>
      ) : null}

      {clients.status !== 'loading' && clients.status !== 'ready' ? (
        <RbacAdminFailureNotice
          failure={clients}
          context="oauth-clients"
          onRetry={() => {
            setReloadToken((token) => token + 1);
          }}
        />
      ) : null}

      {catalog.status !== 'loading' && catalog.status !== 'ready' ? (
        <RbacAdminFailureNotice failure={catalog} context="oauth-clients" />
      ) : null}

      {clients.status === 'ready' && items.length === 0 ? (
        <div className="cap-notice" data-testid="oauth-clients-empty" role="status">
          <StatusBadge tone="neutral">ยังไม่มี client</StatusBadge>
          <p className="cap-notice__text">เซิร์ฟเวอร์ตอบสำเร็จแต่รายการว่างเปล่า — ไม่ใช่การโหลด</p>
        </div>
      ) : null}

      {clients.status === 'ready' && items.length > 0 ? (
        <section className="cap-card cap-card--table" aria-label="OAuth client ทั้งหมด">
          <div className="cap-table-wrap">
            <table className="cap-table" data-testid="oauth-clients-table">
              <caption className="cap-visually-hidden">OAuth client ที่ลงทะเบียนไว้กับ IdP</caption>
              <thead>
                <tr>
                  <th scope="col">client_id</th>
                  <th scope="col">แอป</th>
                  <th scope="col">สถานะ</th>
                  <th scope="col">ต้องมี app role</th>
                  <th scope="col">อายุ token</th>
                  <th scope="col">Redirect URI</th>
                  <th scope="col">MFA role</th>
                  <th scope="col">จัดการ</th>
                </tr>
              </thead>
              <tbody>
                {items.map((client) => (
                  <tr key={client.client_id} data-testid={`oauth-client-row-${client.client_id}`}>
                    <th scope="row" className="cap-table__row-head">
                      {client.client_name}
                      <br />
                      <code className="cap-table__code">{client.client_id}</code>
                    </th>
                    <td>
                      <code className="cap-table__code">{client.app_code}</code>
                    </td>
                    <td>
                      <StatusBadge tone={client.active ? 'success' : 'neutral'}>
                        {client.active ? 'ใช้งาน' : 'ปิดอยู่'}
                      </StatusBadge>
                    </td>
                    <td>{client.require_app_role ? 'ต้องมี' : 'ไม่บังคับ'}</td>
                    <td data-testid={`oauth-client-ttl-${client.client_id}`}>
                      access {describeTtl(client.access_token_ttl_seconds)} / ID{' '}
                      {describeTtl(client.id_token_ttl_seconds)}
                      <br />
                      {client.refresh_enabled
                        ? `refresh สูงสุด ${describeTtl(client.refresh_token_absolute_ttl_seconds ?? 0)} / ว่าง ${describeTtl(client.refresh_token_idle_ttl_seconds ?? 0)}`
                        : 'ไม่ใช้ refresh token'}
                    </td>
                    <td data-testid={`oauth-client-uris-${client.client_id}`}>
                      {client.redirect_uris.map((entry) => (
                        <div key={`${entry.kind}|${entry.uri}`}>
                          <code className="cap-table__code">{entry.uri}</code>{' '}
                          <span>({REDIRECT_URI_KIND_LABELS[entry.kind]})</span>
                        </div>
                      ))}
                    </td>
                    <td>{client.mfa_roles.length === 0 ? '—' : client.mfa_roles.join(', ')}</td>
                    <td>
                      {canEdit ? (
                        <>
                          <button
                            type="button"
                            className="cap-button cap-button--ghost"
                            data-testid={`oauth-client-edit-${client.client_id}`}
                            onClick={() => {
                              openEdit(client);
                            }}
                          >
                            แก้ไข
                          </button>
                          <button
                            type="button"
                            className="cap-button cap-button--ghost"
                            data-testid={`oauth-client-rotate-${client.client_id}`}
                            onClick={() => {
                              setRotateFailure(null);
                              setRotateTarget(client);
                            }}
                          >
                            หมุน secret
                          </button>
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {/* ------------------------------------------------------ ฟอร์มสร้าง/แก้ -- */}

      <Modal
        open={formMode !== null}
        onClose={closeForm}
        title={formMode?.kind === 'edit' ? `แก้ไข client ${formMode.clientId}` : 'สร้าง OAuth client'}
      >
        <form
          className="cap-filters"
          data-testid="oauth-client-form"
          onSubmit={(event) => {
            event.preventDefault();
            void submitForm();
          }}
        >
          {formMode?.kind === 'create' ? (
            <>
              <label className="cap-field">
                <span className="cap-field__label">client_id</span>
                <input
                  className="cap-input"
                  data-testid="oauth-form-client-id"
                  value={form.clientId}
                  autoComplete="off"
                  onChange={(event) => {
                    patchForm({ clientId: event.target.value });
                  }}
                />
              </label>
              <label className="cap-field">
                <span className="cap-field__label">แอป</span>
                <select
                  className="cap-input"
                  data-testid="oauth-form-app-code"
                  value={form.appCode}
                  onChange={(event) => {
                    patchForm({ appCode: event.target.value, mfaRoles: [] });
                  }}
                >
                  <option value="">— เลือกแอป —</option>
                  {appCodes.map((appCode) => (
                    <option key={appCode} value={appCode}>
                      {appCode}
                    </option>
                  ))}
                </select>
              </label>
              <label className="cap-field">
                <span className="cap-field__label">ชื่อ client</span>
                <input
                  className="cap-input"
                  data-testid="oauth-form-client-name"
                  value={form.clientName}
                  autoComplete="off"
                  onChange={(event) => {
                    patchForm({ clientName: event.target.value });
                  }}
                />
              </label>
            </>
          ) : null}

          <fieldset className="cap-field" data-testid="oauth-form-redirects">
            <legend className="cap-field__label">Redirect URI</legend>
            {form.redirectUris.map((entry, index) => (
              <div key={index}>
                <input
                  className="cap-input"
                  aria-label={`redirect URI ${index + 1}`}
                  data-testid={`oauth-form-redirect-uri-${index}`}
                  value={entry.uri}
                  autoComplete="off"
                  onChange={(event) => {
                    setRedirect(index, { uri: event.target.value });
                  }}
                />
                <select
                  className="cap-input"
                  aria-label={`ชนิดของ redirect URI ${index + 1}`}
                  data-testid={`oauth-form-redirect-kind-${index}`}
                  value={entry.kind}
                  onChange={(event) => {
                    setRedirect(index, { kind: event.target.value as RedirectUriKind });
                  }}
                >
                  {REDIRECT_URI_KINDS.map((kind) => (
                    <option key={kind} value={kind}>
                      {REDIRECT_URI_KIND_LABELS[kind]}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="cap-button cap-button--ghost"
                  data-testid={`oauth-form-redirect-remove-${index}`}
                  disabled={form.redirectUris.length <= 1}
                  onClick={() => {
                    setForm((previous) => ({
                      ...previous,
                      redirectUris: previous.redirectUris.filter((_, position) => position !== index),
                    }));
                  }}
                >
                  ลบ
                </button>
              </div>
            ))}
            <button
              type="button"
              className="cap-button cap-button--ghost"
              data-testid="oauth-form-redirect-add"
              onClick={() => {
                setForm((previous) => ({
                  ...previous,
                  redirectUris: [...previous.redirectUris, { uri: '', kind: 'LOGIN_CALLBACK' }],
                }));
              }}
            >
              เพิ่ม redirect URI
            </button>
          </fieldset>

          <fieldset className="cap-field" data-testid="oauth-form-mfa-roles">
            <legend className="cap-field__label">Role ที่บังคับ MFA</legend>
            {rolesOfApp.length === 0 ? (
              <span className="cap-notice__note">
                {form.appCode === '' ? 'เลือกแอปก่อน' : 'แอปนี้ยังไม่มี app role ในแคตตาล็อก'}
              </span>
            ) : (
              rolesOfApp.map((entry) => (
                <label key={entry.role_code}>
                  <input
                    type="checkbox"
                    data-testid={`oauth-form-mfa-role-${entry.role_code}`}
                    checked={form.mfaRoles.includes(entry.role_code)}
                    onChange={(event) => {
                      toggleMfaRole(entry.role_code, event.target.checked);
                    }}
                  />{' '}
                  {entry.role_code}
                </label>
              ))
            )}
          </fieldset>

          <label className="cap-field">
            <span className="cap-field__label">อายุ access token (วินาที)</span>
            <input
              className="cap-input"
              inputMode="numeric"
              data-testid="oauth-form-access-ttl"
              value={form.accessTtl}
              onChange={(event) => {
                patchForm({ accessTtl: event.target.value });
              }}
            />
          </label>
          <label className="cap-field">
            <span className="cap-field__label">อายุ ID token (วินาที)</span>
            <input
              className="cap-input"
              inputMode="numeric"
              data-testid="oauth-form-id-ttl"
              value={form.idTtl}
              onChange={(event) => {
                patchForm({ idTtl: event.target.value });
              }}
            />
          </label>
          <label className="cap-field">
            <input
              type="checkbox"
              data-testid="oauth-form-refresh-enabled"
              checked={form.refreshEnabled}
              onChange={(event) => {
                patchForm({ refreshEnabled: event.target.checked });
              }}
            />
            <span className="cap-field__label">เปิดใช้ refresh token</span>
          </label>
          {form.refreshEnabled ? (
            <>
              <label className="cap-field">
                <span className="cap-field__label">refresh token อายุสูงสุด (วินาที)</span>
                <input
                  className="cap-input"
                  inputMode="numeric"
                  data-testid="oauth-form-refresh-absolute-ttl"
                  value={form.refreshAbsoluteTtl}
                  onChange={(event) => {
                    patchForm({ refreshAbsoluteTtl: event.target.value });
                  }}
                />
              </label>
              <label className="cap-field">
                <span className="cap-field__label">refresh token อายุเมื่อไม่ใช้งาน (วินาที)</span>
                <input
                  className="cap-input"
                  inputMode="numeric"
                  data-testid="oauth-form-refresh-idle-ttl"
                  value={form.refreshIdleTtl}
                  onChange={(event) => {
                    patchForm({ refreshIdleTtl: event.target.value });
                  }}
                />
              </label>
            </>
          ) : null}
          <label className="cap-field">
            <input
              type="checkbox"
              data-testid="oauth-form-require-app-role"
              checked={form.requireAppRole}
              onChange={(event) => {
                patchForm({ requireAppRole: event.target.checked });
              }}
            />
            <span className="cap-field__label">ต้องมี app role จึงล็อกอินแอปนี้ได้</span>
          </label>
          {formMode?.kind === 'edit' ? (
            <label className="cap-field">
              <input
                type="checkbox"
                data-testid="oauth-form-active"
                checked={form.active}
                onChange={(event) => {
                  patchForm({ active: event.target.checked });
                }}
              />
              <span className="cap-field__label">เปิดใช้งาน client</span>
            </label>
          ) : null}

          {formError === null ? null : (
            <p className="cap-notice__text" role="alert" data-testid="oauth-form-error">
              {formError}
            </p>
          )}
          {saveFailure === null ? null : (
            <RbacAdminFailureNotice failure={saveFailure} context="save-oauth-client" />
          )}

          <button
            type="submit"
            className="cap-button cap-button--primary"
            data-testid="oauth-form-submit"
            disabled={pending}
          >
            {pending ? 'กำลังบันทึก…' : formMode?.kind === 'edit' ? 'บันทึก' : 'สร้าง client'}
          </button>
        </form>
      </Modal>

      {/* ------------------------------------------------------- ยืนยันหมุน secret -- */}

      <ConfirmModal
        open={rotateTarget !== null}
        title="หมุน client secret"
        confirmLabel="หมุน secret"
        busy={pending}
        onConfirm={() => {
          void confirmRotate();
        }}
        onCancel={() => {
          setRotateTarget(null);
          setRotateFailure(null);
        }}
      >
        <p className="cap-notice__text" data-testid="oauth-rotate-warning">
          secret เดิมของ <code className="cap-table__code">{rotateTarget?.client_id}</code> จะ
          <strong>ใช้ไม่ได้ทันที</strong> แอปปลายทางต้องเปลี่ยนไปใช้ secret ใหม่ก่อนจึงจะล็อกอินต่อได้
        </p>
        {rotateFailure === null ? null : (
          <RbacAdminFailureNotice failure={rotateFailure} context="rotate-secret" />
        )}
      </ConfirmModal>

      {/* ------------------------------------------------ secret ที่แสดงครั้งเดียว -- */}

      <Modal open={issued !== null} onClose={closeIssued} title="Client secret (แสดงครั้งเดียว)">
        <div data-testid="oauth-secret-modal">
          <p className="cap-notice__text" data-testid="oauth-secret-warning" role="alert">
            <strong>secret นี้จะไม่แสดงอีก</strong> —{' '}
            {issued?.reason === 'rotated' ? 'secret ใหม่ของ' : 'secret ของ'}{' '}
            <code className="cap-table__code">{issued?.clientId}</code> คัดลอกไปเก็บในที่ปลอดภัยตอนนี้
            ปิดหน้าต่างนี้แล้วดูซ้ำไม่ได้ ต้องหมุน secret ใหม่เท่านั้น
          </p>
          <code className="cap-table__code" data-testid="oauth-secret-value">
            {issued?.secret}
          </code>
          <div className="cap-notice__actions">
            <button
              type="button"
              className="cap-button cap-button--primary"
              data-testid="oauth-secret-copy"
              onClick={() => {
                void copySecret();
              }}
            >
              {copied ? 'คัดลอกแล้ว' : 'คัดลอก'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
